import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { PermissionImpact } from '../types/permissionsList.types';

interface PermissionImpactDialogProps {
  impact: PermissionImpact | null;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function PermissionImpactDialog({ impact, isLoading, onConfirm, onClose }: PermissionImpactDialogProps) {
  return (
    <ConfirmDialog
      isOpen={Boolean(impact)}
      title="Xác nhận thay đổi quyền"
      confirmText={impact?.nextEnabled ? 'Bật quyền' : 'Tắt quyền'}
      cancelText="Hủy"
      isLoading={isLoading}
      isDestructive={impact?.risk === 'Cao' || impact?.risk === 'Rất cao'}
      onConfirm={onConfirm}
      onClose={onClose}
      message={impact ? (
        <div className="min-w-0 space-y-2 text-left text-sm">
          <p className="font-semibold text-slate-900">{impact.permissionLabel}</p>
          <div className="grid grid-cols-2 gap-2">
            <Info label="Vai trò" value={impact.role.name} />
            <Info label="Ảnh hưởng" value={`${impact.affectedUsers.length} người`} />
            <Info label="Rủi ro" value={impact.risk} />
            <Info label="Phạm vi" value={impact.scopes.join(', ')} />
          </div>
          {(impact.dependencyWarnings.length > 0 || impact.conflictWarnings.length > 0) && (
            <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              {[...impact.dependencyWarnings, ...impact.conflictWarnings].map((warning) => (
                <p key={warning} className="break-words">{warning}</p>
              ))}
            </div>
          )}
          {impact.autoIncludedDependencies.length > 0 && (
            <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
              <p className="font-semibold">Hệ thống sẽ tự bật thêm quyền phụ thuộc:</p>
              {impact.autoIncludedDependencies.map((dependency) => (
                <p key={dependency} className="break-words">{dependency}</p>
              ))}
            </div>
          )}
          {impact.affectedUsers.length > 0 && (
            <div className="max-h-28 overflow-auto rounded-md border border-slate-100 p-3">
              <p className="mb-2 font-semibold text-slate-700">Người dùng bị ảnh hưởng</p>
              <div className="space-y-1">
                {impact.affectedUsers.slice(0, 8).map((user) => (
                  <p key={user.id} className="truncate text-slate-500">{user.fullName} {user.email ? `(${user.email})` : ''}</p>
                ))}
                {impact.affectedUsers.length > 8 && (
                  <p className="text-slate-400">và {impact.affectedUsers.length - 8} người khác</p>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}
    />
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md bg-slate-50 p-3 text-sm">
      <p className="text-slate-500">{label}</p>
      <p className="truncate font-semibold text-slate-900">{value}</p>
    </div>
  );
}
